<script setup>
import { ref, computed, onMounted } from 'vue'
import User from '@/api/User'
import moment from 'moment'
import AllQueriesView from '@/components/SlideOver/AllQueriesView.vue'
import ContentBlock from '@/components/common/ContentBlock.vue'
import SyntaxHighlightedInput from '@/components/Query/SyntaxHighlightedInput.vue'
import SourceSelector from '@/components/common/SourceSelector.vue'
import TimeRangeDropdown from '@/components/EventSearch/TimeRangeDropdown.vue'
import { extractTimeFromQuery, formatTimeRange } from '@/components/EventSearch/timeRangeUtils'
import { formatSourcesLabel } from '@/components/EventSearch/sourcesLabelUtils'
import { useDataSourcesStore } from '@product/stores/dataSources'
import { useMainStore } from '@/stores/main'
import { getLogTypeColor } from '@/constants/logTypes'

const mainStore = useMainStore()
const dataSourcesStore = useDataSourcesStore()

const props = defineProps({
  fieldMappings: {
    type: Object,
    default: () => ({})
  }
})

// run-search  — the card and the Examples tab: search with whatever sources the
//               card's selector currently holds.
// execute-query — the History tab: re-run a stored query against the sources it
//               was originally run with.
const emit = defineEmits(['run-search', 'execute-query', 'explore-log-type', 'explore-source'])

// An example carries the query; the card carries the scope. The protocol ones are
// the exception — they're a log type and a window rather than a query, so they
// bring their own `sources` and `time` and override the card.
const LAST_7D = { type: 'preset', value: '7d' }

const EXAMPLE_GROUPS = [
  {
    icon: '🌏',
    label: 'Hunt by country or region',
    examples: [
      {
        title: 'Recent covert traffic targeting Ukraine',
        query: 'domain:*.ua OR rcptto:*.ua OR id.resp_h_cc:"UA"',
        description: 'Any flow where the destination domain, SMTP recipient, or resolved destination country is Ukraine.'
      },
      {
        title: 'Recent covert traffic originating from Russia',
        query: 'id.orig_h_cc:"RU"',
        description: 'All flows whose source IP geolocates to Russia.'
      }
    ]
  },
  {
    icon: '🌐',
    label: 'Hunt by a specific entity',
    examples: [
      {
        title: 'Recent covert traffic targeting an organization',
        query: 'domain:(example.com OR *.example.com)',
        description: "Replace example.com with the target's apex domain. Include the wildcard variant to catch subdomains."
      },
      {
        title: 'Recent covert traffic targeting Ukrainian government entities',
        query: 'domain:*.gov.ua OR rcptto:*.gov.ua',
        description: 'Covert flows targeting the .gov.ua zone.'
      }
    ]
  },
  {
    icon: '⛪️',
    label: 'Hunt by sector',
    examples: [
      {
        title: 'Recent covert traffic targeting South Asian telcos',
        query: 'domain:(bsnl.co.in OR ntc.net.np OR robi.com.bd OR zong.com.pk OR grameenphone.com OR slt.lk OR jio.com OR airtel.in OR dialog.lk OR singtel.com OR *.bsnl.co.in OR *.ntc.net.np OR *.robi.com.bd OR *.zong.com.pk OR *.grameenphone.com OR *.slt.lk OR *.jio.com OR *.airtel.in OR *.dialog.lk OR *.singtel.com)',
        description: 'Any flow touching the primary domain or subdomains of the largest South Asian telecom operators.'
      }
    ]
  },
  {
    icon: '💻',
    label: 'Hunt by IP address',
    examples: [
      {
        title: 'Recent covert traffic to or from one or more IPs',
        query: 'ip:(173.239.196.98 OR 62.113.194.134 OR 45.134.140.151 OR 37.19.214.4 OR 173.239.196.102 OR 37.19.200.167)',
        description: 'Combine multiple IPs with OR. Matches both source and destination.'
      },
      {
        title: 'Recent covert traffic to or from a network range',
        query: 'ip:173.239.196.*',
        description: 'Wildcards match any host within the given /24 prefix.'
      }
    ]
  },
  {
    icon: '🫆',
    label: 'Hunt by JA4+ fingerprint',
    examples: [
      {
        title: 'Victims of APT28 initial access efforts',
        query: 'ja4:("t13d121000_a538d02d76d5_5ac7197df9d2" OR "t13d122000_a538d02d76d5_81737722f97c" OR "t13i3211h1_b0a840187ebd_8d633dac7124" OR "t13d4312h1_c7886603b240_24695f2957a7") AND server_name:(*mail* OR *autodiscover* OR *login* OR *owa*)',
        description: 'Traffic matching known APT28 TLS client fingerprints hitting victims around the world. Replace with any JA4 fingerprint.'
      }
    ]
  },
  {
    icon: '🔎',
    label: 'Hunt by protocol',
    examples: [
      {
        title: 'Recent RDP traffic',
        query: '*',
        sources: 'l:rdp',
        scope: 'rdp',
        time: LAST_7D,
        description: 'All observed RDP sessions in the last 7 days.'
      },
      {
        title: 'Recent SMTP traffic',
        query: '*',
        sources: 'l:smtp',
        scope: 'smtp',
        time: LAST_7D,
        description: 'All observed SMTP transactions in the last 7 days.'
      },
      {
        title: 'Recent SNMP traffic',
        query: '*',
        sources: 'l:snmp',
        scope: 'snmp',
        time: LAST_7D,
        description: 'All observed SNMP traffic in the last 7 days.'
      }
    ]
  },
  {
    icon: '🔤',
    label: 'Hunt in cleartext network traffic',
    examples: [
      {
        title: 'Identify recent scripted cleartext traffic flows',
        query: 'user_agent:(*curl* OR *wget* OR *python* OR *Go-http*)',
        description: 'Common scripting and automation user-agents seen crossing covert infrastructure.'
      },
      {
        title: 'Identify successful HTTP login attempts',
        query: 'uri:(*login* OR *admin*) AND status_code:200',
        description: 'HTTP requests to authentication endpoints that returned a 200 response.'
      }
    ]
  },
  {
    icon: '🕵️',
    label: 'Hunt for actors chaining anonymization',
    examples: [
      {
        title: 'Traffic destined for SOCKS proxies',
        query: 'id.resp_p:1080',
        description: 'Flows targeting the standard SOCKS port. Within Covert Network Flows this indicates actors chaining a SOCKS proxy on top of another covert network to further obscure their origin.'
      },
      {
        title: 'Traffic to Tor SOCKS/control ports',
        query: 'id.resp_p:(9050 OR 9051 OR 9150)',
        description: 'Connections to standard Tor client ports, suggesting a Tor hop layered on top of the observed covert channel.'
      }
    ]
  }
]

// --- the search card ---------------------------------------------------------

const queryText = ref('')
// null is All Time — see TimeRangeDropdown.
const timeRange = ref(null)

const canSearch = computed(() => queryText.value.trim().length > 0)

function submit() {
  if (!canSearch.value) return
  emit('run-search', queryText.value, timeRange.value)
}

// --- tabs --------------------------------------------------------------------

// Every tab is a list of the same row card, so they all share one inset.
const ROW_PADDING = '0.75rem 1rem'

const TABS = [
  { key: 'history', label: 'History', icon: ['fas', 'clock-rotate-left'] },
  { key: 'examples', label: 'Examples', icon: ['fas', 'crosshairs'] },
  { key: 'sources', label: 'Sources', icon: ['fas', 'database'] },
  { key: 'logtypes', label: 'Log Types', icon: ['fas', 'layer-group'] }
]

// Starts on History so the strip has an active tab from the first paint; falls
// back to Examples once we know there's no history to show (see loadRecent).
const activeTab = ref('history')
const userPickedTab = ref(false)

function selectTab(key) {
  userPickedTab.value = true
  activeTab.value = key
}

// --- history -----------------------------------------------------------------

const recentQueries = ref([])
const recentLoading = ref(false)

async function fetchRecentQueries() {
  recentLoading.value = true
  try {
    const response = await User.getQueries(0, 8, '', '', '', 'event')
    if (response.status === 'success') {
      recentQueries.value = response.data?.queries || []
    }
  } catch (err) {
    console.error('Failed to fetch recent queries:', err)
  } finally {
    recentLoading.value = false
    // Don't yank the tab out from under someone who clicked while this was in
    // flight.
    if (!userPickedTab.value && recentQueries.value.length === 0) {
      activeTab.value = 'examples'
    }
  }
}

function handleRecentQueryClick(storedQuery, sources) {
  emit('execute-query', storedQuery, sources)
}

// Event Search history: event queries only (excludes selector searches). The
// slideover routes a picked query itself, so there's no emit back through here.
function openHistory() {
  mainStore.show_slideover(AllQueriesView, { filterType: 'event' }, { width: 50 }, 'Search History')
}

function formatTime(timestamp) {
  if (!timestamp) return ''
  return moment(timestamp).fromNow()
}

function parseQuery(queryString) {
  const { cleanQuery, start, end } = extractTimeFromQuery(queryString)
  return {
    text: cleanQuery || queryString,
    timeLabel: formatTimeRange(start, end)
  }
}

// --- examples ----------------------------------------------------------------

// One flat list to render. The groups are how the set is maintained and they
// still set the order, so same-emoji rows sit together and read as a group —
// but each row carries its group's emoji, and its name on hover, rather than
// the list being broken up by headers.
const examples = EXAMPLE_GROUPS.flatMap(group =>
  group.examples.map(ex => ({ ...ex, icon: group.icon, category: group.label }))
)

function runExample(ex) {
  // Only the protocol examples name a scope of their own; everything else runs
  // against whatever the card has selected rather than resetting it.
  if (ex.sources) dataSourcesStore.deserializeSources(ex.sources)
  emit('run-search', ex.query, ex.time ?? timeRange.value)
}

// --- sources / log types -----------------------------------------------------

const availableLogTypes = computed(() => {
  return Object.keys(props.fieldMappings).sort()
})

function getColor(logType) {
  return getLogTypeColor(logType)
}

function handleLogTypeClick(logType) {
  emit('explore-log-type', logType)
}

const sourceGroups = computed(() => {
  const items = [...dataSourcesStore.activeSourceGroups]
  // Include standalone sources as source-like entries
  for (const s of dataSourcesStore.activeStandaloneSources) {
    items.push({ name: s.name, display_name: s.display_name, sources: [], hide_sources: false })
  }
  return items
})

function handleSourceClick(sourceName) {
  emit('explore-source', sourceName)
}

// Owned-group drill-in: reveal member sources so a specific one can be explored.
const expandedGroups = ref({})

function isExpandableGroup(name) {
  return !!dataSourcesStore.expandableGroups[name]
}

function getGroupMembers(name) {
  return dataSourcesStore.expandableGroups[name] || []
}

function toggleGroupExpand(name) {
  expandedGroups.value = {
    ...expandedGroups.value,
    [name]: !expandedGroups.value[name]
  }
}

function handleMemberClick(memberName) {
  emit('explore-source', memberName)
}

onMounted(async () => {
  // The card owns the source selector now, so the store has to be populated
  // here — the toolbar that used to do it isn't mounted on this route.
  await dataSourcesStore.init()
  fetchRecentQueries()
})
</script>

<template>
  <div class="landing-view">
    <div class="search-stage">
      <div class="search-head">
        <h1 class="stage-title">Explore Network Events</h1>
        <p class="stage-sub">Search directly across all raw data from your connected sources to uncover threats and anomalies</p>

        <div class="flat-input">
          <SyntaxHighlightedInput
            v-model="queryText"
            class="flat-well"
            multiline
            submit-on-enter
            placeholder='domain:*.ua OR rcptto:*.ua OR id.resp_h_cc:"UA"'
            @submit="submit"
          />

          <div class="flat-foot">
            <SourceSelector always-enabled />
            <TimeRangeDropdown v-model="timeRange" always-enabled />

            <button
              class="btn-search"
              :disabled="!canSearch"
              @click="submit"
            >
              Search →
            </button>
          </div>
        </div>
      </div>

      <div class="landing-tabs">
        <div class="tab-strip">
          <button
            v-for="tab in TABS"
            :key="tab.key"
            class="tab-btn"
            :class="{ active: activeTab === tab.key }"
            @click="selectTab(tab.key)"
          >
            <font-awesome-icon :icon="tab.icon" />
            {{ tab.label }}
          </button>

          <!-- Lives in the strip rather than under the panel: the strip's height
               is fixed, so History gaining a "View all" can't resize the stage
               and shunt the card. -->
          <button
            v-if="activeTab === 'history' && recentQueries.length > 0"
            class="view-all-btn"
            @click="openHistory"
          >
            View all →
          </button>
        </div>

        <div class="tab-panel">
          <!-- History -->
          <template v-if="activeTab === 'history'">
            <div v-if="recentLoading" class="panel-state">
              <font-awesome-icon :icon="['fas', 'spinner']" spin />
            </div>
            <div v-else-if="recentQueries.length === 0" class="panel-state">
              No searches yet — try an example.
            </div>
            <div v-else class="panel-list">
              <ContentBlock
                v-for="(q, i) in recentQueries"
                :key="q.id || i"
                interactive
                :padding="ROW_PADDING"
                @click="handleRecentQueryClick(q.query, q.sources)"
              >
                <div class="recent-row">
                  <div class="recent-query-content">
                    <code class="recent-query">{{ parseQuery(q.query).text }}</code>
                    <span class="time-badge">{{ parseQuery(q.query).timeLabel }}</span>
                    <span v-if="formatSourcesLabel(q.sources)" class="sources-badge">{{ formatSourcesLabel(q.sources) }}</span>
                  </div>
                  <span class="recent-time">{{ formatTime(q.timestamp) }}</span>
                </div>
              </ContentBlock>
            </div>
          </template>

          <!-- Examples -->
          <div v-else-if="activeTab === 'examples'" class="panel-list">
            <ContentBlock
              v-for="ex in examples"
              :key="ex.title"
              interactive
              :padding="ROW_PADDING"
              @click="runExample(ex)"
            >
              <div class="example-row">
                <div class="example-text">
                  <span class="example-title">
                    <span class="example-emoji" :title="ex.category">{{ ex.icon }}</span>{{ ex.title }}
                  </span>
                  <span class="example-desc">{{ ex.description }}</span>
                </div>

                <div class="example-query">
                  <!-- title carries the full query: the long ones truncate. -->
                  <code class="example-code" :title="ex.query">{{ ex.query }}</code>
                  <span v-if="ex.time" class="time-badge">{{ ex.time.value }}</span>
                  <span v-if="ex.scope" class="sources-badge">{{ ex.scope }}</span>
                </div>
              </div>
            </ContentBlock>
          </div>

          <!-- Sources -->
          <template v-else-if="activeTab === 'sources'">
            <div v-if="sourceGroups.length === 0" class="panel-state">
              No sources connected.
            </div>
            <div v-else class="panel-list">
              <ContentBlock
                v-for="group in sourceGroups"
                :key="group.name"
                interactive
                :padding="ROW_PADDING"
                @click="handleSourceClick(group.name)"
              >
                <div class="source-row">
                  <font-awesome-icon :icon="['fas', 'database']" class="source-icon" />
                  <span class="source-label">{{ group.display_name }}</span>
                  <button
                    v-if="isExpandableGroup(group.name)"
                    type="button"
                    class="group-expand-toggle"
                    :title="expandedGroups[group.name] ? 'Hide sources' : 'Show sources'"
                    @click.stop="toggleGroupExpand(group.name)"
                  >
                    <font-awesome-icon
                      :icon="['fas', expandedGroups[group.name] ? 'chevron-up' : 'chevron-down']"
                    />
                  </button>
                </div>

                <!-- Member sources (owned group drill-in) -->
                <div
                  v-if="isExpandableGroup(group.name) && expandedGroups[group.name]"
                  class="group-members"
                >
                  <button
                    v-for="member in getGroupMembers(group.name)"
                    :key="member.name"
                    type="button"
                    class="group-member"
                    @click.stop="handleMemberClick(member.token)"
                  >
                    <font-awesome-icon :icon="['fas', 'database']" class="group-member-icon" />
                    <span class="group-member-label">{{ member.display_name }}</span>
                  </button>
                </div>
              </ContentBlock>
            </div>
          </template>

          <!-- Log Types -->
          <template v-else-if="activeTab === 'logtypes'">
            <div v-if="availableLogTypes.length === 0" class="panel-state">
              No log types available.
            </div>
            <div v-else class="panel-list">
              <ContentBlock
                v-for="lt in availableLogTypes"
                :key="lt"
                interactive
                :padding="ROW_PADDING"
                @click="handleLogTypeClick(lt)"
              >
                <div class="log-type-row">
                  <span class="log-type-color" :style="{ backgroundColor: getColor(lt) }"></span>
                  <span class="log-type-name">{{ lt }}</span>
                </div>
              </ContentBlock>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* The stage vocabulary below mirrors SelectorSearchView's landing so the two
   search entry points read as one system. */

.landing-view {
  height: 100%;
  width: 100%;
  overflow-y: auto;
}

/* min-height rather than height: the stage always fills the viewport so the tab
   area can reach the bottom edge, but on a short screen it grows past it and
   .landing-view scrolls instead of crushing the card. */
.search-stage {
  max-width: 940px;
  width: 100%;
  min-height: 100%;
  margin: 0 auto;
  padding: 3rem 1.5rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Takes its content height, never shrinks, and shares the free space with the
   tabs below. flex-end keeps that share as clearance *above* the hero — which is
   what lands the card near the middle of the screen — instead of splitting it
   above and below and leaving a hole between the card and the tabs. */
.search-head {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 1.5rem;
}

/* Hero title mirrors the home page hero-greeting (Jura 700, ~2.75rem). */
.stage-title {
  font-family: 'Jura', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: var(--text-heading);
  text-align: center;
  margin: 0;
}

.stage-sub {
  text-align: center;
  font-size: var(--fs-xl);
  color: var(--text-secondary);
  margin: -0.5rem 0 0;
}

/* ===== search card ===================================================== */

/* Same floor as Selector Search's card, so the two search entries stand at the
   same size. The well (flex:1) absorbs everything the foot doesn't use. */
.flat-input {
  position: relative;
  background: var(--surface-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  display: flex;
  flex-direction: column;
  min-height: 22.5rem;
  /* One height for every control in the foot — dropdowns and button alike. */
  --foot-control-h: 2.75rem;
  transition: border-color 0.15s ease;
}

.flat-input:focus-within {
  border-color: var(--accent);
}

/* The input's three text layers share their metrics through these custom
   properties (see SyntaxHighlightedInput) — setting them here moves all three
   together onto Selector Search's well metrics. */
/* basis:0 rather than auto — the input's multiline rule sets height:100%, which
   as a flex-basis would have it demand the whole card and shrink the foot out
   from under its own controls. This way it takes exactly what the foot leaves. */
.flat-well {
  flex: 1 1 0;
  min-height: 6rem;
}

/* The class lands on the input's own root, so this needs to out-specify the
   component's `.syntax-input-container.multiline` rule to move the metrics. */
.flat-input .flat-well.syntax-input-container {
  --sqi-pad: 1.75rem 1.9rem;
  --sqi-font: var(--fs-xl);
  --sqi-line: 1.7;
  height: 100%;
}

.flat-foot {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.85rem 1rem;
  flex: none;
}

/* Every control in the foot stands at one height. .dropdown-trigger otherwise
   sizes itself off its padding (and stretches to a toolbar row), which left the
   two dropdowns shorter than the button beside them. */
.flat-foot :deep(.dropdown-trigger),
.btn-search {
  align-self: center;
  height: var(--foot-control-h);
  box-sizing: border-box;
}

/* ===== search button =================================================== */

.btn-search {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 1.4rem;
  border: none;
  border-radius: var(--radius-lg);
  background: var(--accent-soft-3);
  color: var(--accent-text);
  font-size: var(--fs-lg);
  font-weight: var(--fw-semibold);
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex: none;
  margin-left: auto;
  transition: background 0.15s ease, color 0.15s ease;
}

.btn-search:hover:not(:disabled) {
  background: var(--accent-soft-4);
}

/* Always mounted — with nothing typed it stands greyed rather than vanishing,
   so the card always shows what it's for. */
.btn-search:disabled {
  background: var(--surface-overlay-2);
  color: var(--text-disabled);
  cursor: not-allowed;
}

/* ===== tabs ============================================================ */

/* PageToolbar's .tab-btn atom is :deep()-scoped to that component and there's no
   toolbar on this page, so the same values are restated here. Keep the two in
   sync if the atom moves. */
.tab-strip {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  /* Fixed height, so a tab whose panel offers a "View all" doesn't change the
     strip's size and reflow the stage. */
  height: 2.5rem;
  flex: none;
  margin-bottom: 0.75rem;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 0.875rem;
  box-sizing: border-box;
  background: transparent;
  border: none;
  border-radius: var(--radius-lg);
  color: var(--text-secondary);
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  font-family: inherit;
  cursor: pointer;
  transition: var(--transition-fast);
  white-space: nowrap;
}

.tab-btn:hover:not(.active) {
  background: var(--surface-hover);
  color: white;
}

.tab-btn.active {
  background: var(--accent-soft);
  color: white;
  font-weight: var(--fw-semibold);
}

.tab-btn.active svg {
  color: var(--accent-text);
}

/* Takes the free space the head doesn't and runs to the bottom edge. The list
   length never feeds back into the layout — a long Log Types tab and an empty
   History tab give the panel exactly the same box.

   The 9rem basis rides the card up off dead centre: head and tabs split what's
   left after it, so seeding the tabs with 9rem costs the head half that and
   lifts the card ~4.5rem. The tabs take the space back, so the gap to the card
   stays the stage gap. */
.landing-tabs {
  flex: 1 1 9rem;
  min-height: 12rem;
  display: flex;
  flex-direction: column;
}

.tab-panel {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--surface-control) transparent;
}

.tab-panel::-webkit-scrollbar {
  width: 11px;
}

.tab-panel::-webkit-scrollbar-track {
  background: transparent;
}

.tab-panel::-webkit-scrollbar-thumb {
  /* Inset via a transparent border so the thumb reads as a slim pill, not a
     bar flush to the panel edge. */
  background: var(--surface-control);
  border-radius: var(--radius-pill);
  border: 3px solid transparent;
  background-clip: padding-box;
}

.tab-panel::-webkit-scrollbar-thumb:hover {
  background: var(--border-strong);
  background-clip: padding-box;
}

.panel-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

/* Centered in the panel rather than sitting at the top of an otherwise empty
   box, now that the panel is as tall as the space below the card. */
.panel-state {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--text-muted);
  font-size: var(--fs-base);
}

/* ===== rows ============================================================ */

/* Every tab is a list of the same dark card: the row shell is
   <ContentBlock interactive>, so only the inner layout lives here. */

.recent-row,
.example-row,
.source-row,
.log-type-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

/* --- history --- */

.recent-row {
  justify-content: space-between;
  gap: 1rem;
}

.recent-query-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  overflow: hidden;
}

.recent-query {
  color: var(--accent-text);
  font-size: var(--fs-md);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

/* Shared with the protocol example cards, which are scoped rather than typed. */
.time-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  background: var(--accent-soft-3);
  border-radius: var(--radius-sm);
  font-size: var(--fs-2xs);
  color: var(--accent-text);
  white-space: nowrap;
  flex-shrink: 0;
}

.sources-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  background: var(--color-success-soft);
  border-radius: var(--radius-sm);
  font-size: var(--fs-2xs);
  color: var(--color-success-text);
  white-space: nowrap;
  flex-shrink: 0;
}

.recent-time {
  font-size: var(--fs-sm);
  color: var(--text-faint);
  white-space: nowrap;
  flex-shrink: 0;
}

.view-all-btn {
  margin-left: auto;
  background: transparent;
  border: none;
  color: var(--accent-text);
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  font-family: inherit;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-xs);
  transition: var(--transition-fast);
}

.view-all-btn:hover {
  background: var(--accent-soft-2);
}

/* --- examples --- */

/* The group's emoji stands in for a section header; its name is the tooltip.
   Inline in the title, so the description below aligns to the title's own edge
   rather than being indented past an emoji column. */
.example-emoji {
  margin-right: 0.45rem;
  cursor: help;
}

.example-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.example-title {
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  color: var(--text-primary);
}

/* Wraps rather than truncates — the row grows to fit it. */
.example-desc {
  font-size: var(--fs-sm);
  color: var(--text-muted);
  line-height: var(--lh-tight);
}

/* Held to a share of the row so a 500-character query can't crowd out the title
   it belongs to; it ellipsises and the full text is on the tooltip. */
.example-query {
  flex: 0 1 40%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
}

.example-code {
  flex: 0 1 auto;
  min-width: 0;
  padding: 0.375rem 0.5rem;
  background: var(--accent-soft);
  border-radius: var(--radius-sm);
  color: var(--accent-text);
  font-size: var(--fs-xs);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* --- sources --- */

.source-icon {
  flex: none;
  width: 1rem;
  text-align: center;
  color: var(--text-faint);
  font-size: var(--fs-sm);
}

.source-label {
  flex: 1;
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-expand-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--radius-xs);
  color: var(--text-faint);
  font-size: var(--fs-2xs);
  cursor: pointer;
  transition: var(--transition-fast);
  flex-shrink: 0;
}

.group-expand-toggle:hover {
  color: var(--text-primary);
  background: var(--surface-overlay-2);
}

.group-members {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-subtle);
}

.group-member {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.625rem;
  background: var(--surface-overlay-2);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-pill);
  color: var(--text-secondary);
  font-size: var(--fs-sm);
  font-family: inherit;
  cursor: pointer;
  transition: var(--transition-fast);
  white-space: nowrap;
}

.group-member:hover {
  background: var(--accent-soft);
  border-color: var(--accent-soft-4);
  color: var(--text-heading);
}

.group-member-icon {
  font-size: var(--fs-xs);
  color: var(--text-faint);
  flex-shrink: 0;
}

.group-member-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* --- log types --- */

/* Log-type swatches come from the shared data-viz palette, which is
   deliberately literal rather than tokenized. */
.log-type-color {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-xs);
  flex-shrink: 0;
}

.log-type-name {
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  color: var(--text-primary);
  text-transform: uppercase;
}

/* ===== motion ========================================================== */

/* This view's own transitions. The row cards' hover transition belongs to
   ContentBlock and is scoped there, so it isn't reachable from here. */
@media (prefers-reduced-motion: reduce) {
  .flat-input,
  .btn-search,
  .tab-btn,
  .view-all-btn,
  .group-expand-toggle,
  .group-member {
    transition: none;
  }
}
</style>
